import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import {
  Plus,
  Sparkles,
  PlayCircle,
  Eye,
  Pencil,
  Trash2,
  ArrowLeft,
  Check,
  X,
  Download,
  Upload,
  RefreshCw,
  FileText,
  Filter,
  MoreHorizontal,
  MoreVertical,
  ChevronDown,
  ExternalLink,
  Copy,
  Save,
  Send,
  LucideIcon,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface BaseButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  size?: 'sm' | 'default' | 'lg' | 'icon';
}

const LoadingSpinner = () => <Loader2 className="h-4 w-4 animate-spin" />;

// ============================================================================
// PRIMARY ACTIONS - Dark solid buttons for main CTAs
// ============================================================================

export const PrimaryButton: React.FC<BaseButtonProps & { icon?: LucideIcon }> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  type = 'button',
  size = 'sm',
  icon: Icon,
}) => (
  <Button
    type={type}
    variant="default"
    size={size}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={cn('gap-2', className)}
  >
    {isLoading ? <LoadingSpinner /> : Icon && <Icon className="h-4 w-4" />}
    {children}
  </Button>
);

export const CreateButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { children, onClick, disabled, isLoading, className, size = 'sm' } = props;
    return (
      <Button
        ref={ref}
        variant="default"
        size={size}
        onClick={onClick}
        disabled={disabled || isLoading}
        className={cn('gap-2', className)}
      >
        {isLoading ? <LoadingSpinner /> : <Plus className="h-4 w-4" />}
        {children ?? t('common.create')}
      </Button>
    );
  }
);
CreateButton.displayName = 'CreateButton';

export const SaveButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  type = 'submit',
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      type={type}
      variant="default"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Save className="h-4 w-4" />}
      {children ?? t('common.save')}
    </Button>
  );
};

export const ProcessButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="default"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <PlayCircle className="h-4 w-4" />}
      {isLoading ? t('common.processing') : (children ?? t('common.process'))}
    </Button>
  );
};

// ============================================================================
// SECONDARY ACTIONS - Outline buttons for secondary actions
// ============================================================================

export const SecondaryButton: React.FC<BaseButtonProps & { icon?: LucideIcon }> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  type = 'button',
  size = 'sm',
  icon: Icon,
}) => (
  <Button
    type={type}
    variant="outline"
    size={size}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={cn('gap-2', className)}
  >
    {isLoading ? <LoadingSpinner /> : Icon && <Icon className="h-4 w-4" />}
    {children}
  </Button>
);

export const AddButton = React.forwardRef<HTMLButtonElement, BaseButtonProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { children, onClick, disabled, isLoading, className, size = 'sm' } = props;
    return (
      <Button
        ref={ref}
        variant="outline"
        size={size}
        onClick={onClick}
        disabled={disabled || isLoading}
        className={cn('gap-2', className)}
      >
        {isLoading ? <LoadingSpinner /> : <Plus className="h-4 w-4" />}
        {children ?? t('common.add')}
      </Button>
    );
  }
);
AddButton.displayName = 'AddButton';

export const GenerateButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Sparkles className="h-4 w-4" />}
      {isLoading ? t('common.generating') : (children ?? t('common.generate'))}
    </Button>
  );
};

export const ViewButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Eye className="h-4 w-4" />}
      {children ?? t('common.view')}
    </Button>
  );
};

export const EditButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Pencil className="h-4 w-4" />}
      {children ?? t('common.edit')}
    </Button>
  );
};

export const ExportButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Download className="h-4 w-4" />}
      {children ?? t('common.export')}
    </Button>
  );
};

export const UploadButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Upload className="h-4 w-4" />}
      {children ?? t('common.upload')}
    </Button>
  );
};

export const RefreshButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <RefreshCw className="h-4 w-4" />}
      {children ?? t('common.refresh')}
    </Button>
  );
};

export const ReportButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <FileText className="h-4 w-4" />}
      {children ?? t('common.report')}
    </Button>
  );
};

export const CopyButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Copy className="h-4 w-4" />}
      {children ?? t('common.copy')}
    </Button>
  );
};

export const OpenExternalButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <ExternalLink className="h-4 w-4" />}
      {children ?? t('common.open')}
    </Button>
  );
};

// ============================================================================
// GHOST BUTTONS - Minimal styling for subtle actions
// ============================================================================

export const GhostButton: React.FC<BaseButtonProps & { icon?: LucideIcon }> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  type = 'button',
  size = 'sm',
  icon: Icon,
}) => (
  <Button
    type={type}
    variant="ghost"
    size={size}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={cn('gap-2', className)}
  >
    {isLoading ? <LoadingSpinner /> : Icon && <Icon className="h-4 w-4" />}
    {children}
  </Button>
);

export const BackButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={cn('gap-2', className)}
    >
      <ArrowLeft className="h-4 w-4" />
      {children ?? t('common.back')}
    </Button>
  );
};

export const CancelButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={cn('gap-2', className)}
    >
      {children ?? t('common.cancel')}
    </Button>
  );
};

// ============================================================================
// DESTRUCTIVE BUTTONS - For delete/danger actions
// ============================================================================

export const DeleteButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2 text-destructive hover:text-destructive hover:bg-destructive/10', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Trash2 className="h-4 w-4" />}
      {children ?? t('common.delete')}
    </Button>
  );
};

interface ConfirmDeleteButtonProps {
  onConfirm: () => void | Promise<void>;
  itemName?: string;
  itemType?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  size?: 'sm' | 'default' | 'lg' | 'icon';
  iconOnly?: boolean;
  title?: string;
  description?: string;
  /** Defaults to the trash can. Pass Unlink where the action detaches a
   *  relation rather than destroying the record. */
  icon?: LucideIcon;
}

export const ConfirmDeleteButton: React.FC<ConfirmDeleteButtonProps> = ({
  onConfirm,
  itemName,
  itemType,
  disabled,
  isLoading,
  className,
  size = 'sm',
  iconOnly = true,
  title,
  description,
  icon: Icon = Trash2,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size={size}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        disabled={disabled || isLoading}
        className={cn('text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10', className)}
      >
        {isLoading ? <LoadingSpinner /> : <Icon className="h-4 w-4" />}
        {!iconOnly && t('common.delete')}
      </Button>
      <DeleteConfirmationDialog
        isOpen={open}
        onOpenChange={setOpen}
        onConfirm={() => { onConfirm(); setOpen(false); }}
        itemName={itemName}
        itemType={itemType}
        title={title}
        description={description}
      />
    </>
  );
};

export const DestructiveButton: React.FC<BaseButtonProps & { icon?: LucideIcon }> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
  icon: Icon,
}) => (
  <Button
    variant="destructive"
    size={size}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={cn('gap-2', className)}
  >
    {isLoading ? <LoadingSpinner /> : Icon && <Icon className="h-4 w-4" />}
    {children}
  </Button>
);

// ============================================================================
// TOGGLE BUTTONS - For validate/approve actions
// ============================================================================

export const ValidateButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="default"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
      {children ?? t('common.validate')}
    </Button>
  );
};

export const DevalidateButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <X className="h-4 w-4" />}
      {children ?? t('common.devalidate')}
    </Button>
  );
};

export const ApproveButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="default"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
      {children ?? t('common.approve')}
    </Button>
  );
};

export const RejectButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  size = 'sm',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2 text-destructive border-destructive/30 hover:bg-destructive/10', className)}
    >
      {isLoading ? <LoadingSpinner /> : <X className="h-4 w-4" />}
      {children ?? t('common.reject')}
    </Button>
  );
};

// ============================================================================
// ICON BUTTONS - Small icon-only buttons
// ============================================================================

export const IconButton: React.FC<BaseButtonProps & { icon: LucideIcon; label: string }> = ({
  onClick,
  disabled,
  isLoading,
  className,
  icon: Icon,
  label,
}) => (
  <Button
    variant="ghost"
    size="icon"
    onClick={onClick}
    disabled={disabled || isLoading}
    className={cn('h-8 w-8', className)}
    aria-label={label}
  >
    {isLoading ? <LoadingSpinner /> : <Icon className="h-4 w-4" />}
  </Button>
);

/** Kebab trigger. Input: button props, forwarded so a Radix
 * `DropdownMenuTrigger asChild` can open it. */
export const MenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof Button> & { horizontal?: boolean }
>(({ className, horizontal = true, ...props }, ref) => (
  <Button
    ref={ref}
    variant="ghost"
    size="sm"
    className={cn('h-8 w-8 p-0', className)}
    {...props}
  >
    {horizontal ? (
      <MoreHorizontal className="h-4 w-4" />
    ) : (
      <MoreVertical className="h-4 w-4" />
    )}
  </Button>
));
MenuButton.displayName = 'MenuButton';

// ============================================================================
// FILTER BUTTON - Standardized filter dropdown trigger
// ============================================================================

export const FilterButton = React.forwardRef<
  HTMLButtonElement,
  BaseButtonProps & {
    label?: string;
    activeCount?: number;
    icon?: LucideIcon;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({
  children,
  onClick,
  disabled,
  className,
  size = 'sm',
  label,
  activeCount,
  icon: Icon = Filter,
  ...props
}, ref) => {
  const { t } = useTranslation();
  return (
    <Button
      ref={ref}
      variant="outline"
      size={size}
      onClick={onClick}
      disabled={disabled}
      {...props}
      className={cn(
        'gap-2 text-xs border-foreground/20 hover:bg-foreground hover:text-background transition-colors',
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children || label || t('common.filters')}
      {activeCount !== undefined && activeCount > 0 && (
        <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-foreground/10 text-[10px]">
          {activeCount}
        </span>
      )}
    </Button>
  );
});
FilterButton.displayName = 'FilterButton';

// ============================================================================
// DROPDOWN TRIGGER - For dropdown menus
// ============================================================================

export const DropdownButton = React.forwardRef<
  HTMLButtonElement,
  BaseButtonProps & { icon?: LucideIcon } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({
  children,
  onClick,
  disabled,
  className,
  size = 'sm',
  icon: Icon,
  ...props
}, ref) => (
  <Button
    ref={ref}
    variant="outline"
    size={size}
    onClick={onClick}
    disabled={disabled}
    {...props}
    className={cn('gap-2', className)}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
    <ChevronDown className="h-3 w-3 opacity-50" />
  </Button>
));
DropdownButton.displayName = 'DropdownButton';

// ============================================================================
// SUBMIT BUTTON - For forms
// ============================================================================

export const SubmitButton: React.FC<BaseButtonProps> = ({
  children,
  onClick,
  disabled,
  isLoading,
  className,
  type = 'submit',
  size = 'default',
}) => {
  const { t } = useTranslation();
  return (
    <Button
      type={type}
      variant="default"
      size={size}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn('gap-2', className)}
    >
      {isLoading ? <LoadingSpinner /> : <Send className="h-4 w-4" />}
      {isLoading ? t('common.submitting') : (children ?? t('common.submit'))}
    </Button>
  );
};
